import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { useTrackEditInCanvasOpened } from '@air/analytics';
import { type Clip } from '@air/api/types';
import {
  type AppNode,
  clipCanOpenInCanvas,
  getAddToCanvasMenuState,
  getAssetDropToastMessage,
} from '@air/feature-edit-canvas';

import { clipsToAppNodes } from '~/components/Modals/AddToCanvasModal/clipsToAppNodes';
import {
  type PendingCanvasEditRequest,
  usePendingCanvasEditStore,
} from '~/components/v2/store/features/canvas/usePendingCanvasEditStore';
import { useDraftCanvas } from '~/hooks/canvases/useDraftCanvas';
import { CentralizedBoardSelectors } from '~/store/centralizedBoard/selectors';
import { canvasOpenLinksToVersionTarget } from '~/utils/canvases/canvasOpenLinksToVersionTarget';
import {
  type EditInCanvasOpenedFrom,
  getEditInCanvasFunnelParams,
  getEditInCanvasOpenedAction,
} from '~/utils/canvases/getEditInCanvasAnalytics';
import {
  type EditInCanvasSizeSetExposure,
  getEditInCanvasOpenedSizeSetExposure,
} from '~/utils/canvases/getEditInCanvasSizeSetExposure';
import { getPendingEditInCanvasNodeId } from '~/utils/canvases/getPendingEditInCanvasNodeId';

export type OpenClipInCanvasParams = PendingCanvasEditRequest & {
  announcementId?: string;
  clips: Clip[];
  /** Pre-built nodes parallel to `clips`, used instead of deriving nodes from the clips (e.g. an unsaved cut). */
  nodes?: AppNode[];
  openedFrom: EditInCanvasOpenedFrom;
  /** Set when opened from the Edit in Canvas submenu. */
  sizeSetExposure?: EditInCanvasSizeSetExposure;
};

type GetInitialCanvasNodesParams = {
  eligibleClips: Clip[];
  clips: Clip[];
  prebuiltNodes: AppNode[] | undefined;
};

const getInitialCanvasNodes = ({ clips, eligibleClips, prebuiltNodes }: GetInitialCanvasNodesParams): AppNode[] => {
  if (!prebuiltNodes) {
    return clipsToAppNodes(eligibleClips);
  }

  return eligibleClips.length === clips.length ? prebuiltNodes : [];
};

export type UseOpenClipInCanvasReturn = {
  openClipInCanvas: (params: OpenClipInCanvasParams) => void;
};

/**
 * Opens a new draft canvas preloaded with the clips that can open in canvas, in fullscreen.
 * Unsupported items are skipped and surfaced with the same drop toast used elsewhere on canvas.
 * Image jobs stash intent for `CanvasPendingEdit` on the first image node — videos in a mixed
 * group still land on the canvas, but the tool is not armed on them. A video-only selection
 * (one clip or many) still gets video jobs (smart resize / convert to GIF). Convert to GIF
 * aims at the first clip within the 30s / 500MB cap, not the first selected video.
 *
 * The canvas inherits the board the user is browsing.
 */
export const useOpenClipInCanvas = (): UseOpenClipInCanvasReturn => {
  const breadcrumbBoardId = useSelector(CentralizedBoardSelectors.boardId);
  const { openDraftCanvas } = useDraftCanvas();
  const { trackEditInCanvasOpened } = useTrackEditInCanvasOpened();

  const openClipInCanvas = useCallback(
    ({
      announcementId,
      clips,
      nodes: prebuiltNodes,
      openedFrom,
      sizeSetExposure,
      ...request
    }: OpenClipInCanvasParams) => {
      const { action } = request;
      const eligibleClips = clips.filter(clipCanOpenInCanvas);
      const nodes = getInitialCanvasNodes({ clips, eligibleClips, prebuiltNodes });
      const [firstNode] = nodes;

      if (!firstNode) {
        return;
      }

      const [onlyClip] = eligibleClips;
      const versionTargetClipId =
        canvasOpenLinksToVersionTarget(openedFrom, eligibleClips.length) && onlyClip ? onlyClip.id : undefined;

      const boardId = breadcrumbBoardId ?? undefined;
      const { source, viewedFrom } = getEditInCanvasFunnelParams(openedFrom);
      const pendingNodeId = getPendingEditInCanvasNodeId({
        action,
        ...(action === 'videoConvert' && {
          canConvertToGif: eligibleClips.map((clip) => getAddToCanvasMenuState([clip]).canAdd),
        }),
        clips: eligibleClips,
        nodeIds: nodes.map((node) => node.id),
      });
      const unsupportedCount = clips.length - eligibleClips.length;
      const insertionToastMessage =
        unsupportedCount > 0 ? getAssetDropToastMessage(eligibleClips.length, unsupportedCount, 'canvas') : undefined;

      if (pendingNodeId) {
        usePendingCanvasEditStore.getState().setPending({ ...request, nodeId: pendingNodeId });
      }

      const openedAction = getEditInCanvasOpenedAction(action);

      trackEditInCanvasOpened({
        action: openedAction,
        clip_count: eligibleClips.length,
        opened_from: openedFrom,
        ...(boardId && { board_id: boardId }),
        ...(sizeSetExposure && getEditInCanvasOpenedSizeSetExposure({ action, sizeSetExposure })),
      });

      openDraftCanvas({
        analyticsContext: {
          edit_in_canvas_action: openedAction,
          source,
          ...(boardId && { board_id: boardId }),
          ...(announcementId ? { announcement_id: announcementId } : {}),
        },
        boardId,
        initialNodes: nodes,
        insertionToastMessage,
        versionTargetClipId,
        viewedFrom,
      });
    },
    [breadcrumbBoardId, openDraftCanvas, trackEditInCanvasOpened],
  );

  return { openClipInCanvas };
};
